import { useEffect, useMemo, useState } from 'react';
import { TemplateType_Slug } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { Commands1 } from '@frontend/api-commands';
import { TemplatorV2Hooks } from '@frontend/api-templator-v2';
import { useTranslation } from '@frontend/i18n';
import { useGetDefaultTemplate } from '@frontend/integrated-messaging';
import { paymentsSentry } from '@frontend/payments-hooks';
import { MultiStepModal } from '@frontend/payments-multistep-modal';
import { theme } from '@frontend/theme';
import { Modal, ModalControlModalProps, useAlert } from '@frontend/design-system';
import { CollectionColumn } from '../common/generate-columns';
import { BULK_CREATE_STEPS } from './bulk-create-steps-key-title';
import { NonSelectableUsersStep } from './steps/non-selectable-users-step';
import { SelectTemplateStep } from './steps/select-template-step';
import { SendSmsStep } from './steps/send-sms-step';
import { ValidateUsersStep } from './steps/validate-users-step';
import { mappedTags, buildBulkCreatePayload, derivePatientGroups, getAllPatientsCount } from './utils';

export type BulkCreateStepId = (typeof BULK_CREATE_STEPS)[keyof typeof BULK_CREATE_STEPS]['id'];

type BulkCreateModalProps = {
  modalProps: ModalControlModalProps;
  selected?: CollectionColumn[];
  locationIds: string[];
  initialStep?: BulkCreateStepId;
};
export const BulkCreateModal = ({ modalProps, selected, locationIds, initialStep }: BulkCreateModalProps) => {
  const alert = useAlert();
  const { t } = useTranslation('payments');
  const { invalidateQueries } = Commands1._QueryUpdaters.useQueryUpdaters();
  const getDefaultTemplate = useGetDefaultTemplate({
    groupIds: locationIds,
  });

  const [template, setTemplate] = useState(() =>
    getDefaultTemplate({ type: TemplateType_Slug.MANUAL_PAYMENT_REQUEST })
  );

  const { data } = TemplatorV2Hooks.useFilteredTemplateTypes({
    templateTypeSlugs: [TemplateType_Slug.MANUAL_PAYMENT_REQUEST],
  });
  const dynamicFields = data?.find((type) => type.slug === TemplateType_Slug.MANUAL_PAYMENT_REQUEST)?.dynamicFields;

  const { mutate: createPaymentRequest, isLoading } = Commands1.Mutations.useBatchCreateBalancesPaymentRequestsMutation(
    {
      options: {
        onSuccess: (result) => {
          if (result.failure?.length) {
            alert.error(t('Some payment requests failed to be created'));
            paymentsSentry.error(
              'Some payment requests failed to be created in bulk create modal',
              'BulkCreateModal',
              `Failed Requests: ${result.failure
                .map((f) => `Patient ID: ${f.patientId}, Reason: ${f.errorMessage}`)
                .join('; ')}`
            );
          }
          if (result.success?.length) {
            alert.success(t('{{count}} payment requests created successfully', { count: result.success.length }));
          }
          invalidateQueries({ endpointName: 'GetPatientsBalance' });
          modalProps.onClose();
        },
        onError: (err) => {
          alert.error(t('Error creating payment requests'));
          paymentsSentry.error(
            'Error creating payment request and sending SMS',
            'BulkCreateModal',
            `${err instanceof Error ? err.message : 'Unknown error'}`
          );
          modalProps.onClose();
        },
      },
    }
  );

  const { data: collections } = Commands1.Queries.useGetPatientsBalanceQuery({
    request: {
      locationIds,
    },
    options: {
      onError: () => {
        alert.error(t('Error fetching collections'));
      },
      enabled: modalProps.show,
    },
  });

  const { patients, patientsUniqueLocations, patientsWithoutPhone } = useMemo(
    () => derivePatientGroups(selected || collections?.patientBalances || []),
    [selected, collections]
  );
  const [internalSelected, setInternalSelected] = useState<Record<string, boolean>>(
    patients.reduce((acc, patient) => ({ ...acc, [patient.patientId]: true }), {})
  );

  const createAndSendRequest = ({ useTTP, smsBody }: { useTTP?: boolean; smsBody?: string }) => {
    const payload = buildBulkCreatePayload({
      patients,
      internalSelected,
      allPatientsCount: getAllPatientsCount(collections?.patientBalances),
      patientsUniqueLocations,
      useTTP,
      smsBody,
    });
    createPaymentRequest(payload);
  };

  useEffect(() => {
    if (modalProps.show) {
      setTemplate(getDefaultTemplate({ type: TemplateType_Slug.MANUAL_PAYMENT_REQUEST }));
    }
  }, [modalProps.show]);

  return (
    <MultiStepModal
      as={Modal.Responsive}
      modalProps={modalProps}
      closeModal={modalProps.onClose}
      initialStep={initialStep ?? BULK_CREATE_STEPS.SEND_SMS.id}
      maxWidth={parseFloat(theme.spacing(97))}
      hideBackButton
    >
      <MultiStepModal.Step id={BULK_CREATE_STEPS.VALIDATE_USERS.id} title={BULK_CREATE_STEPS.VALIDATE_USERS.title}>
        <ValidateUsersStep patients={patients} onSelectionChange={setInternalSelected} selected={internalSelected} />
      </MultiStepModal.Step>
      <MultiStepModal.Step id={BULK_CREATE_STEPS.SEND_SMS.id} title={BULK_CREATE_STEPS.SEND_SMS.title}>
        <SendSmsStep
          count={Object.values(internalSelected).filter(Boolean).length}
          text={template?.templateString ?? ''}
          onSend={createAndSendRequest}
          isLoading={isLoading}
          tags={dynamicFields ? mappedTags(dynamicFields) : []}
        />
      </MultiStepModal.Step>
      <MultiStepModal.Step id={BULK_CREATE_STEPS.SELECT_TEMPLATE.id} title={BULK_CREATE_STEPS.SELECT_TEMPLATE.title}>
        <SelectTemplateStep locationIds={locationIds} onSelectTemplate={setTemplate} />
      </MultiStepModal.Step>
      <MultiStepModal.Step
        id={BULK_CREATE_STEPS.NON_SELECTABLE_USERS.id}
        title={t('{{count}} Balances', { count: patientsWithoutPhone.length })}
      >
        <NonSelectableUsersStep data={patientsWithoutPhone} />
      </MultiStepModal.Step>
    </MultiStepModal>
  );
};
